import { getTimeRangeFromDurationString, TimeRangeValue } from '@chronosphereio/core';
import { PanelDefinition } from '@/core';
import { useRelativeTimeQueryOption } from '@/dashboards/utils';
import { TimeRangeProvider, useTimeRange } from '@/plugin-system/runtime/TimeRangeProviderV2';

/**
 * Encapsulates logic for overriding parent time range based on query_options.time.start in panel spec.
 */
export function TimeOverrideProvider(props: {
  panelDefinition: PanelDefinition;
  /**
   * Can be used to disable the check for query_options.time.start when a nested TimeRangeProvider is needed
   * regardless of whether the query option is defined.
   * @default true
   */
  checkQueryOptionDefined?: boolean;
  onTimeRangeChange?: (newTimeRange: TimeRangeValue) => void;
  children: React.ReactNode;
}) {
  const { panelDefinition, checkQueryOptionDefined = true, onTimeRangeChange, children } = props;

  const { absoluteTimeRange, timeRange: dashboardTimeRange } = useTimeRange();

  const relativeTimeQueryOption = useRelativeTimeQueryOption(panelDefinition);
  const isRelativeTimeOverrideDefined = relativeTimeQueryOption !== undefined;

  // Only nest TimeRangeProvider when query_options.time.start is set
  if (checkQueryOptionDefined && !isRelativeTimeOverrideDefined) {
    return <>{children}</>;
  }

  const activeTimeRange = isRelativeTimeOverrideDefined
    ? getTimeRangeFromDurationString(relativeTimeQueryOption)
    : dashboardTimeRange;

  return (
    <TimeRangeProvider
      defaultTimeRange={activeTimeRange}
      enableUrlParams={false}
      recomputeKey={`${absoluteTimeRange.end}${relativeTimeQueryOption}`}
      onTimeRangeChange={onTimeRangeChange}
      enableReinitialize={true}
    >
      {children}
    </TimeRangeProvider>
  );
}
